import { useEffect, useRef, useCallback } from 'react';
import {
  setupSmartAnimationTrigger,
  applyCachedAnimation,
  promoteToRenderLayer,
  batchPromoteElements,
  cacheAnimationState,
  applyCachedAnimationState,
  getAnimationCacheStats
} from '../utils/animationCacheService';

interface UseAnimationCacheOptions {
  enabled?: boolean;
  performanceMode?: 'high' | 'medium' | 'low';
  autoPromoteToRenderLayer?: boolean;
  logStats?: boolean;
}

interface AnimationTarget {
  element: HTMLElement;
  animation: string;
  delay?: number;
  options?: {
    duration?: number;
    easing?: string;
    iterations?: number;
    fill?: FillMode;
  };
}

interface IntersectionOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export const useAnimationCache = (options: UseAnimationCacheOptions = {}) => {
  const {
    enabled = true,
    performanceMode = 'high',
    autoPromoteToRenderLayer = true,
    logStats = process.env.NODE_ENV === 'development'
  } = options;

  const elementsRef = useRef<Map<string, HTMLElement>>(new Map());
  const observersRef = useRef<IntersectionObserver[]>([]);

  // Register element for animations
  const registerElement = useCallback((key: string, element: HTMLElement) => {
    if (!enabled || performanceMode === 'low') return;
    
    elementsRef.current.set(key, element);
    
    if (autoPromoteToRenderLayer) {
      promoteToRenderLayer(element, true);
    }
  }, [enabled, performanceMode, autoPromoteToRenderLayer]);

  // Apply cached animation to registered element
  const animateElement = useCallback((
    key: string, 
    animationName: string, 
    options?: {
      duration?: number;
      easing?: string;
      iterations?: number;
      fill?: FillMode;
    }
  ) => {
    if (!enabled || performanceMode === 'low') return null;
    
    const element = elementsRef.current.get(key);
    if (!element) {
      console.warn(`Element ${key} not registered for animation`);
      return null;
    }

    return applyCachedAnimation(element, animationName, options);
  }, [enabled, performanceMode]);

  // Batch animate multiple elements
  const batchAnimate = useCallback((targets: AnimationTarget[]) => {
    if (!enabled || performanceMode === 'low') return;
    
    const elements = targets.map(target => target.element).filter(Boolean);
    if (elements.length > 0) {
      batchPromoteElements(elements, true);
    }

    targets.forEach((target, index) => {
      const delay = target.delay || index * 100;
      setTimeout(() => {
        applyCachedAnimation(target.element, target.animation, target.options);
      }, delay);
    });
  }, [enabled, performanceMode]);

  // Setup scroll-triggered animations
  const setupScrollAnimations = useCallback((
    elements: HTMLElement[],
    animationName: string,
    intersectionOptions?: IntersectionOptions
  ) => {
    if (!enabled || performanceMode === 'low') return;
    
    const observer = setupSmartAnimationTrigger(
      elements, 
      animationName, 
      intersectionOptions
    );
    observersRef.current.push(observer);
    
    return observer;
  }, [enabled, performanceMode]);

  // Cache current state of element
  const cacheElementState = useCallback((key: string, includeComputedStyle = false) => {
    if (!enabled) return;
    
    const element = elementsRef.current.get(key);
    if (element) {
      cacheAnimationState(key, element, includeComputedStyle);
    }
  }, [enabled]);

  // Apply cached state to element
  const applyCachedState = useCallback((key: string, skipIfRecent = 100) => {
    if (!enabled) return false;
    
    const element = elementsRef.current.get(key);
    if (element) {
      return applyCachedAnimationState(key, element, skipIfRecent);
    }
    return false;
  }, [enabled]);

  // Get performance statistics
  const getStats = useCallback(() => {
    return getAnimationCacheStats();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observersRef.current.forEach(observer => observer.disconnect());
      observersRef.current = [];
      elementsRef.current.clear();
    };
  }, []);

  // Log stats in development
  useEffect(() => {
    if (logStats && enabled) {
      const stats = getStats();
      console.log('🎬 Animation Cache Stats:', stats);
    }
  }, [logStats, enabled, getStats]);

  return {
    registerElement,
    animateElement,
    batchAnimate,
    setupScrollAnimations,
    cacheElementState,
    applyCachedState,
    getStats,
    enabled: enabled && performanceMode !== 'low'
  };
}; 